import React from 'react';
import PropTypes from 'prop-types';
import CircularProgress from '@material-ui/core/CircularProgress';
import ArrowDropDown from '@material-ui/icons/ArrowDropDown';
import ArrowDropUp from '@material-ui/icons/ArrowDropUp';

import iconButtonStyle, { ButtonCaret } from './IconButton.style';
import ButtonBase from '../ButtonBase/ButtonBase';

const types = ['primary', 'secondary', 'error'];

const IconButton = React.forwardRef(
  (
    {
      icon,
      selected,
      disabled,
      loading,
      label,
      className,
      size,
      type,
      onClick,
      onBlur,
      onFocus,
      onMouseLeave,
      onMouseOver,
      onTouchEnd,
      onTouchStart,
      onMouseDown,
      disableRipple,
      buttonAttributes,
      onKeyDown,
      caret,
      'data-testid': dataTestId,
      'aria-describedby': ariaDescribedBy,
    },
    ref
  ) => {
    const Icon = icon;
    return (
      <ButtonBase
        ref={ref}
        css={[
          iconButtonStyle.container.base,
          iconButtonStyle.container.bySize(caret)[size],
        ]}
        context="iconButton"
        type={types.includes(type) ? type : 'secondary'}
        className={className}
        disabled={disabled}
        selected={selected}
        aria-label={label}
        onClick={onClick}
        onBlur={onBlur}
        onFocus={onFocus}
        onMouseLeave={onMouseLeave}
        onMouseOver={onMouseOver}
        onTouchEnd={onTouchEnd}
        onTouchStart={onTouchStart}
        onMouseDown={onMouseDown}
        onKeyDown={onKeyDown}
        disableRipple={disableRipple}
        data-testid={dataTestId}
        aria-describedby={ariaDescribedBy}
        {...buttonAttributes}
      >
        {loading ? (
          <CircularProgress
            color="inherit"
            size={iconButtonStyle.loader.bySize[size]}
          />
        ) : (
          <Icon css={iconButtonStyle.content.bySize[size]} />
        )}
        {caret !== undefined && (
          <ButtonCaret>
            {caret === 'up' ? (
              <ArrowDropUp fontSize="small" />
            ) : (
              <ArrowDropDown fontSize="small" />
            )}
          </ButtonCaret>
        )}
      </ButtonBase>
    );
  }
);

IconButton.defaultProps = {
  type: 'secondary',
  disabled: false,
  selected: false,
  loading: false,
  className: undefined,
  size: 'medium',
  caret: undefined,
  onClick: () => {},
  onMouseDown: () => {},
  onKeyDown: () => {},
  onBlur: () => {},
  onFocus: () => {},
  onMouseLeave: () => {},
  onMouseOver: () => {},
  onTouchEnd: () => {},
  onTouchStart: () => {},
  disableRipple: true,
  buttonAttributes: {},
  'data-testid': '',
  'aria-describedby': undefined,
};

IconButton.propTypes = {
  /** The main style of the button */
  type: PropTypes.oneOf(types),

  /** The button size */
  size: PropTypes.oneOf(['xSmall', 'small', 'medium']),

  /** Is the loading component displayed */
  loading: PropTypes.bool,

  /** Is the button disabled */
  disabled: PropTypes.bool,

  /** Is the button selected */
  selected: PropTypes.bool,

  /** The content of the button */
  icon: PropTypes.elementType.isRequired,

  /** Custom class name to include own styles */
  className: PropTypes.string,

  /** Button aria-label */
  label: PropTypes.string.isRequired,

  /** Button aria-describedby */
  'aria-describedby': PropTypes.string,

  /** On click callback */
  onClick: PropTypes.func,

  /** On mouse down callback */
  onMouseDown: PropTypes.func,

  /** On blur callback */
  onBlur: PropTypes.func,

  /** On focus callback */
  onFocus: PropTypes.func,

  /** On mouse leave callback */
  onMouseLeave: PropTypes.func,

  /** On mouse over callback */
  onMouseOver: PropTypes.func,

  /** On touch end callback */
  onTouchEnd: PropTypes.func,

  /** On touch start callback */
  onTouchStart: PropTypes.func,

  /** On key down callback */
  onKeyDown: PropTypes.func,

  /** Disable the Material UI ripple effect */
  disableRipple: PropTypes.bool,

  /** Displays caret if values are provided. Can be up or down */
  caret: PropTypes.oneOf(['up', 'down']),

  /** Custom button attributes */
  buttonAttributes: PropTypes.object,

  'data-testid': PropTypes.string,
};

IconButton.displayName = 'IconButton';

export default React.memo(IconButton);
